import React, { useState } from 'react';
import { backendApi } from '../api/backendApi';
import { useQuery } from '@tanstack/react-query';
import { Search, RotateCcw } from 'lucide-react';
import { Button } from '../Components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../Components/ui/card';
import { Input } from '../Components/ui/input';
import { Label } from '../Components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../Components/ui/select';
import { RadioGroup, RadioGroupItem } from '../Components/ui/radio-group';
import { Checkbox } from '../Components/ui/checkbox';
import { Tabs, TabsList, TabsTrigger } from '../Components/ui/tabs';

export default function AdvancedSearch() {
  const [searchType, setSearchType] = useState('locations');
  const [keywords, setKeywords] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [country, setCountry] = useState('United States');
  const [state, setState] = useState('');
  const [city, setCity] = useState('');
  const [includeWithoutCountry, setIncludeWithoutCountry] = useState(false);
  const [searchHistory, setSearchHistory] = useState([
    '("UI UX" OR "UI/UX Designer" OR "User Interface Designer") AND "Figma" AND "React" AND ("HTML" OR "CSS") AND "wireframe" AND ("portfolio" OR "design system" OR "prototype")United States',
    '("UI UX" OR "UI/UX DESIGNER" OR "USER INTERFACE DESIGNER") AND...'
  ]);

  useQuery({
    queryKey: ['applicants'],
    queryFn: () => backendApi.entities.Applicant.list('-created_date', 100),
    initialData: []
  });

  const handleSearch = () => {
    // Add to search history
    const searchQuery = `${keywords} ${jobTitle} ${country}`;
    if (searchQuery.trim()) {
      setSearchHistory(prev => [searchQuery, ...prev.slice(0, 9)]);
    }
  };

  const handleReset = () => {
    setKeywords('');
    setJobTitle('');
    setState('');
    setCity('');
    setIncludeWithoutCountry(false);
  };

  return (
    <div className="max-w-full mx-auto">
      {/* Header Tabs */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800">
        <div className="flex items-center">
          <Tabs defaultValue="internaldb" className="flex-1">
            <TabsList className="!bg-transparent border-b-0 rounded-none w-full justify-start h-auto p-0">
              <TabsTrigger 
                value="internaldb" 
                className="!bg-white !text-blue-900 !shadow-none rounded-none px-6 py-3 border-r border-blue-500/30"
              >
                Internal DB
              </TabsTrigger>
              <TabsTrigger 
                value="career" 
                className="!bg-transparent !text-white/80 !shadow-none rounded-none px-6 py-3 border-r border-blue-500/30 hover:!bg-blue-700 hover:!text-white transition-colors"
              >
                Career Builder
              </TabsTrigger>
              <TabsTrigger 
                value="monster" 
                className="!bg-transparent !text-white/80 !shadow-none rounded-none px-6 py-3 border-r border-blue-500/30 hover:!bg-blue-700 hover:!text-white transition-colors"
              >
                Monster
              </TabsTrigger>
              <TabsTrigger 
                value="dice" 
                className="!bg-transparent !text-white/80 !shadow-none rounded-none px-6 py-3 border-r border-blue-500/30 hover:!bg-blue-700 hover:!text-white transition-colors"
              >
                Dice
              </TabsTrigger>
              <TabsTrigger 
                value="techfetch" 
                className="!bg-transparent !text-white/80 !shadow-none rounded-none px-6 py-3 border-r border-blue-500/30 hover:!bg-blue-700 hover:!text-white transition-colors"
              >
                TechFetch
              </TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="px-4 py-3">
            <Input
              placeholder="Search..."
              className="h-9 w-64 bg-white/90 border-0 focus:bg-white"
            />
          </div>
        </div>
      </div>

      {/* Search Form */}
      <div 
        className="relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 50%, #2563eb 100%)',
          backgroundSize: '200% 200%'
        }}
      >
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>

        <div className="relative px-8 py-6">
          <div className="mb-4">
            <h1 className="text-2xl font-bold text-white mb-1 flex items-center space-x-2">
              <Search className="h-6 w-6" />
              <span>APPLICANT SEARCH</span>
            </h1>
            <div className="flex items-center space-x-3 text-sm text-white/80">
              <button className="hover:text-white transition-colors">Search Options</button>
              <span>|</span>
              <button className="hover:text-white transition-colors">Exclude</button>
            </div>
          </div>

          <Card className="shadow-2xl border-0">
            <CardContent className="p-6">
              <div className="grid grid-cols-2 gap-6 mb-4">
                <div>
                  <Label className="text-gray-700 text-xs font-semibold mb-2 block uppercase tracking-wide">KEYWORDS</Label>
                  <Input
                    value={keywords}
                    onChange={(e) => setKeywords(e.target.value)}
                    placeholder="JavaScript"
                    className="h-10 border-gray-300"
                  />
                </div>
                <div>
                  <Label className="text-gray-700 text-xs font-semibold mb-2 block uppercase tracking-wide">JOB TITLE</Label>
                  <Input
                    value={jobTitle}
                    onChange={(e) => setJobTitle(e.target.value)}
                    placeholder="Java Developer"
                    className="h-10 border-gray-300"
                  />
                </div>
              </div>

              <div className="mb-4">
                <button className="text-blue-600 text-xs font-semibold hover:underline flex items-center">
                  + STRING BUILDER
                </button>
              </div>

              <div className="mb-5">
                <RadioGroup value={searchType} onValueChange={setSearchType} className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="locations" id="locations" />
                    <Label htmlFor="locations" className="cursor-pointer text-sm font-normal">
                      Search Within Selected Locations
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2 ml-6">
                    <RadioGroupItem value="radius" id="radius" />
                    <Label htmlFor="radius" className="cursor-pointer text-sm font-normal">
                      Search Within Radius
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              <div className="grid grid-cols-3 gap-6 mb-5">
                <div>
                  <Label className="text-gray-700 text-xs font-semibold mb-2 block uppercase tracking-wide">COUNTRY</Label>
                  <Select value={country} onValueChange={setCountry}>
                    <SelectTrigger className="h-10 border-gray-300">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="United States">United States</SelectItem>
                      <SelectItem value="Canada">Canada</SelectItem>
                      <SelectItem value="United Kingdom">United Kingdom</SelectItem>
                      <SelectItem value="India">India</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-700 text-xs font-semibold mb-2 block uppercase tracking-wide">STATE</Label>
                  <Select value={state} onValueChange={setState}>
                    <SelectTrigger className="h-10 border-gray-300">
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Texas">Texas</SelectItem>
                      <SelectItem value="California">California</SelectItem>
                      <SelectItem value="New York">New York</SelectItem>
                      <SelectItem value="Florida">Florida</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-700 text-xs font-semibold mb-2 block uppercase tracking-wide">CITY</Label>
                  <Input
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="Enter city"
                    className="h-10 border-gray-300"
                  />
                </div>
              </div>

              <div className="mb-6">
                <button className="text-blue-600 text-xs font-semibold hover:underline">
                  + ADVANCED OPTIONS
                </button>
                <div className="mt-3 space-y-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="without-country"
                      checked={includeWithoutCountry}
                      onCheckedChange={setIncludeWithoutCountry}
                    />
                    <Label htmlFor="without-country" className="cursor-pointer text-sm font-normal">
                      Include Applicants Without Country
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox id="recent-title" defaultChecked />
                    <Label htmlFor="recent-title" className="cursor-pointer text-sm font-normal">
                      Recent Job Title
                    </Label>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
                <Button 
                  variant="outline" 
                  onClick={handleReset}
                  className="h-10 px-8 border-gray-300 font-semibold"
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  RESET
                </Button>
                <Button 
                  onClick={handleSearch}
                  className="h-10 px-8 bg-blue-600 hover:bg-blue-700 font-semibold"
                >
                  <Search className="h-4 w-4 mr-2" />
                  SEARCH
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Search History */}
      <div className="grid grid-cols-2 gap-6 p-6 bg-gray-50">
        <Card className="shadow-sm">
          <CardHeader className="border-b bg-white px-6 py-4">
            <CardTitle className="text-base font-bold text-gray-800 uppercase tracking-wide">RECENT HISTORY</CardTitle>
          </CardHeader>
          <CardContent className="p-6 bg-white">
            {searchHistory.length > 0 ? (
              <div className="space-y-4">
                {searchHistory.map((query, index) => (
                  <div key={index} className="flex items-start space-x-3 group cursor-pointer hover:bg-gray-50 p-2 rounded -mx-2">
                    <Search className="h-4 w-4 text-blue-600 mt-1 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-blue-600 hover:underline line-clamp-2">{query}</p>
                      <p className="text-xs text-gray-500 mt-1">10:24:25 14:45:01</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No recent searches</p>
            )}
          </CardContent>
        </Card>

        <Card className="shadow-sm">
          <CardHeader className="border-b bg-white px-6 py-4">
            <CardTitle className="text-base font-bold text-gray-800 uppercase tracking-wide">SAVED HISTORY</CardTitle>
          </CardHeader>
          <CardContent className="p-6 bg-white">
            <p className="text-gray-500 text-center py-8">No history found</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}